"use client";

import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiArrowLeft } from "react-icons/fi";
import Navbar from "../../../../../components/navbar.jsx";
import Footer from "../../../../../components/footer.jsx";
import Button from "../../../../../components/Button.jsx";
import FormInput from "../../../../../components/FormInput.jsx";
import ConfirmPaymentModal from "../../../components/ConfirmPaymentModal.jsx";
import PaymentCompleteModal from "../../../components/PaymentCompleteModal.jsx";
import PrintReceiptDialog from "../../../../../components/print/views/PrintReceiptDialog";
import styles from "../css/cashPayment.module.css";
import { getPaymentTotal as apiGetPaymentTotal, setPaymentTotal as apiSetPaymentTotal, previewCash as apiPreviewCash, confirmCash as apiConfirmCash, completeCodPayment as apiCompleteCodPayment, clearCart as apiClearCart, startSplitPayment as apiStartSplitPayment, getSplitState as apiGetSplitState, confirmSplitPayment as apiConfirmSplitPayment } from "../../../../../api/frontShop/CheckOutAPI.js";
import api from "../../../../../api";
import { recordDebtorsPayment } from "../../../../../api/frontShop/DebtorsAPI.js";

export default function CashPaymentView() {
  const location = useLocation();
  const navigate = useNavigate();

  const [total, setTotal] = useState(0);
  const [mode, setMode] = useState("nearest"); // 'nearest' | 'other'
  const [inputValue, setInputValue] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [confirmResult, setConfirmResult] = useState(null);
  const [amountPaidUsed, setAmountPaidUsed] = useState(0);
  const [splitOutstanding, setSplitOutstanding] = useState(null);
  const [printOpen, setPrintOpen] = useState(false);
  const [receiptNo, setReceiptNo] = useState("");

  const fromSplit = !!(location.state && location.state.splitFlow);
  const method1 =
    location.state && typeof location.state.method1 === "string"
      ? location.state.method1
      : "cash";
  const fromDebtorsSplit = !!(location.state && location.state.fromDebtors);
  const fromCOD = !!(location.state && location.state.fromCOD);
  const isSplitMethod1Cash = fromSplit && method1 === "cash";
  const isDebtorsDirect = location.pathname.startsWith("/frontshop/debtors/payment-types/cash");
  const isSplitMethod2Cash = location.pathname.startsWith("/frontshop/payment-types/split/step2/method2=cash");

  useEffect(() => {
    let cancelled = false;
    async function loadTotal() {
      try {
        const isDebtors = location.pathname.startsWith("/frontshop/debtors/payment-types/cash");

        // Coming from COD: always respect the passed total (do not overwrite with checkout session total)
        if (fromCOD && location.state && typeof location.state.total === "number") {
          setTotal(location.state.total);
          return;
        }

        // When on split step 2 (method2=cash), fetch outstanding and auto-fill Amount Given
        if (isSplitMethod2Cash) {
          try {
            const { data: json } = await apiGetSplitState();
            if (json?.success && json?.data) {
              const outstanding = Number(json.data.outstanding) || 0;
              // Keep Total Due as original checkout total when passed via navigation
              if (location.state && typeof location.state.total === "number") {
                setTotal(Number(location.state.total));
              }
              setSplitOutstanding(outstanding);
              setInputValue(outstanding.toFixed(2));
            }
          } catch { }
        }
        if (location.state && typeof location.state.total === "number") {
          setTotal(location.state.total);
        }
        if (!isDebtors) {
          const { data: json } = await apiGetPaymentTotal();
          if (!cancelled && json?.success && typeof json.total === "number") {
            setTotal(json.total);
          }
          await apiSetPaymentTotal();
        }
      } catch { }
    }
    loadTotal();
    return () => {
      cancelled = true;
    };
  }, [location.state]);

  const nearestAmount = useMemo(() => {
    if (!total) return 0;
    return Math.ceil(total / 10) * 10; // nearest higher 10
  }, [total]);

  useEffect(() => {
    if (mode === "nearest" && !isSplitMethod1Cash && !isSplitMethod2Cash) {
      setInputValue(nearestAmount ? nearestAmount.toFixed(2) : "");
    }
  }, [mode, nearestAmount, isSplitMethod1Cash, isSplitMethod2Cash]);

  const numericGiven = useMemo(() => {
    const cleaned = String(inputValue).replace(",", ".");
    const value = parseFloat(cleaned);
    return Number.isFinite(value) ? value : 0;
  }, [inputValue]);

  // Clamp amount given to Total Due in Debtors flow
  useEffect(() => {
    if (!isDebtorsDirect) return;
    if (!Number.isFinite(numericGiven)) return;
    if (total > 0 && numericGiven > total) {
      setInputValue(total.toFixed(2));
    }
    if (numericGiven < 0) {
      setInputValue("0.00");
    }
  }, [isDebtorsDirect, numericGiven, total]);

  const [changeDue, setChangeDue] = useState(0);

  const effectiveAmountGiven = useMemo(() => {
    if (isSplitMethod1Cash || isDebtorsDirect || isSplitMethod2Cash) {
      return numericGiven;
    }
    return mode === "nearest" ? nearestAmount : numericGiven;
  }, [isSplitMethod1Cash, isDebtorsDirect, isSplitMethod2Cash, mode, nearestAmount, numericGiven]);

  const balanceOutstanding = useMemo(() => {
    // In split step 2 (method2=cash), display the actual outstanding from session
    if (isSplitMethod2Cash && splitOutstanding != null) {
      return Math.max(Number(splitOutstanding) || 0, 0);
    }
    const useBalanceMode = fromSplit || isDebtorsDirect || isSplitMethod2Cash;
    if (!useBalanceMode) return 0;
    if (!Number.isFinite(effectiveAmountGiven) || effectiveAmountGiven < 0) {
      return 0;
    }
    const diff = total - effectiveAmountGiven;
    return diff > 0 ? diff : 0;
  }, [fromSplit, isDebtorsDirect, isSplitMethod2Cash, total, effectiveAmountGiven, splitOutstanding]);

  const isFullyCoveredInSplit =
    isSplitMethod1Cash && numericGiven >= total && total > 0;

  useEffect(() => {
    let cancelled = false;
    async function preview() {
      try {
        const isManualFlow = isSplitMethod1Cash || isDebtorsDirect || isSplitMethod2Cash;
        const amt = isManualFlow
          ? numericGiven
          : mode === "nearest"
            ? nearestAmount
            : numericGiven;

        // COD payments don't use the cart session totals on the backend, so compute change locally
        if (fromCOD) {
          const change = +(Math.max(0, (Number(amt) || 0) - (Number(total) || 0))).toFixed(2);
          setChangeDue(change);
          return;
        }

        if (!Number.isFinite(amt) || amt < 0) {
          setChangeDue(0);
          return;
        }
        const { data: json } = await apiPreviewCash(amt);
        if (!cancelled && json?.success && json?.data) {
          setChangeDue(Number(json.data.change || 0));
        }
      } catch { }
    }
    preview();
    return () => {
      cancelled = true;
    };
  }, [mode, nearestAmount, numericGiven, isSplitMethod1Cash, isDebtorsDirect, isSplitMethod2Cash, fromCOD, total]);

  const handleBack = () => {
    if (fromSplit) {
      navigate("/frontshop/payment-types/split", {
        state: { total, fromDebtors: fromDebtorsSplit },
      });
      return;
    }
    if (fromCOD) {
      navigate("/frontshop/payment-types/cod/payment", { state: { total } });
      return;
    }
    if (fromDebtorsSplit) {
      navigate("/frontshop/debtors/payment-types", {
        state: { total, fromDebtors: true },
      });
      return;
    }
    navigate("/frontshop/payment-types", { state: { total } });
  };

  const handleMarkPaid = async () => {
    if (isSplitMethod1Cash) {
      if (isFullyCoveredInSplit) {
        setShowConfirm(true);
        return;
      }
      try {
        await apiStartSplitPayment(Number(numericGiven) || 0);
      } catch { }
      navigate("/frontshop/payment-types/split/step2", {
        state: { total, method1, fromDebtors: fromDebtorsSplit },
      });
      return;
    }

    if (fromSplit) {
      navigate("/frontshop/payment-types/split/step2", {
        state: { total, method1, fromDebtors: fromDebtorsSplit },
      });
      return;
    }
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    setError("");
    setLoading(true);
    try {
      if (fromCOD) {
        const rec = location.state && typeof location.state.recieptno === "string" ? location.state.recieptno : "";
        if (!rec) throw new Error("Missing receipt number for COD payment");
        const tendered = mode === "nearest" ? nearestAmount : numericGiven;
        await apiCompleteCodPayment(rec, "CASH", tendered);
        setShowConfirm(false);
        setShowComplete(true);
        setReceiptNo(String(rec));
        setPrintOpen(true);
        return;
      }

      const amtBase = (isSplitMethod1Cash || isDebtorsDirect)
        ? numericGiven
        : mode === "nearest"
          ? nearestAmount
          : numericGiven;
      if (isDebtorsDirect) {
        const amountToPay = Math.max(0, Math.min(total, Number(amtBase) || 0));
        const balance = Math.max(0, Number((total - amountToPay).toFixed(2)));
        let pid = null;
        try {
          const ss = sessionStorage.getItem("debtors_patientid");
          if (ss) pid = Number(ss) || null;
          if (!pid) {
            const raw = localStorage.getItem("frontshop_customer");
            if (raw) pid = JSON.parse(raw)?.patientid || null;
          }
        } catch { }
        if (!pid) throw new Error("No patient selected");
        const { data: json } = await recordDebtorsPayment(pid, { amount: amountToPay, balance });
        setAmountPaidUsed(amountToPay);
        setConfirmResult(json?.data || null);
        setShowConfirm(false);
        setShowComplete(true);
        setReceiptNo("");
        setPrintOpen(false);
      } else if (isSplitMethod2Cash) {
        // Finalize split as method2 = CASH
        const { data: json } = await apiConfirmSplitPayment("CASH");
        setShowConfirm(false);
        setShowComplete(true);
        const rec = json?.data?.recieptno ? String(json.data.recieptno) : "";
        if (rec) {
          setReceiptNo(rec);
          setPrintOpen(true);
        }
      } else {
        const { data: json } = await apiConfirmCash(amtBase);
        setConfirmResult(json?.data || null);
        setShowConfirm(false);
        setShowComplete(true);
        const rec = json?.data?.recieptno ? String(json.data.recieptno) : "";
        if (rec) {
          setReceiptNo(rec);
          setPrintOpen(true);
        }
      }
    } catch (e) {
      setError(e?.message || "Payment failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.wrapper}>
      <Navbar onBack={handleBack} />

      <main className={styles.main}>
        <div className={styles.card}>
          <div className={styles.header}>
            <h1 className={styles.title}>Cash Payment</h1>
          </div>

          <section className={styles.totalSection}>
            <div className={styles.totalBox}>
              <div className={styles.totalLabel}>Total Due</div>
              <div className={styles.totalAmount}>R {total.toFixed(2)}</div>
            </div>
          </section>

          <section className={styles.amountSection}>
            <div className={styles.sectionLabel}>Amount Given</div>
            {isSplitMethod1Cash || isDebtorsDirect || isSplitMethod2Cash ? (
              <div className={styles.amountInputRowFull}>
                <FormInput
                  label=""
                  type="text"
                  name="cashAmountSplitMethod1"
                  value={inputValue}
                  onChange={(e) => {
                    const raw = e.target.value || "";
                    const cleaned = raw.replace(/[^0-9.,]/g, "");
                    setInputValue(cleaned);
                  }}
                  prefix="R"
                  className={styles.amountInputCustom}
                />
              </div>
            ) : (
              <div className={styles.amountCard}>
                <div className={styles.amountOptionsRow}>
                  <button
                    type="button"
                    className={`${styles.optionButton} ${mode === "nearest" ? styles.optionActive : ""}`}
                    onClick={() => setMode("nearest")}
                  >
                    R {nearestAmount.toFixed(2)}
                  </button>
                  <button
                    type="button"
                    className={`${styles.optionButton} ${mode === "other" ? styles.optionActive : ""}`}
                    onClick={() => setMode("other")}
                  >
                    Other
                  </button>
                </div>

                {mode === "other" && (
                  <div className={styles.amountInputRow}>
                    <div className={styles.amountInputLabel}>Amount</div>
                    <FormInput
                      label=""
                      type="text"
                      name="cashAmountOther"
                      value={inputValue}
                      onChange={(e) => {
                        const raw = e.target.value || "";
                        // Allow only digits, dot, and comma
                        const cleaned = raw.replace(/[^0-9.,]/g, "");
                        setInputValue(cleaned);
                      }}
                      prefix="R"
                      className={styles.amountInputCustom}
                    />
                  </div>
                )}
              </div>
            )}
          </section>

          <section className={styles.changeSection}>
            <div className={styles.changeBox}>
              <div className={styles.changeLabel}>
                {fromSplit || isDebtorsDirect || isSplitMethod2Cash ? "Balance Outstanding" : "Change Due"}
              </div>
              <div className={styles.changeAmount}>
                R {((fromSplit || isDebtorsDirect || isSplitMethod2Cash) ? balanceOutstanding : changeDue).toFixed(2)}
              </div>
            </div>
          </section>

          <div className={styles.buttonsRow}>
            <Button
              type="button"
              variant="cancel"
              className={styles.backBtn}
              onClick={handleBack}
            >
              <FiArrowLeft style={{ marginRight: 6 }} />
              Back
            </Button>
            <Button
              type="button"
              variant="approve"
              className={styles.payBtn}
              onClick={handleMarkPaid}
            >
              {isFullyCoveredInSplit
                ? "Mark as paid"
                : isSplitMethod1Cash
                  ? "Mark as partially paid"
                  : isDebtorsDirect && total > 0 && effectiveAmountGiven < total
                    ? "Mark as partially paid"
                    : "Mark as paid"}
            </Button>
          </div>
        </div>
      </main>

      <div className={styles.footerWrapper}>
        <Footer />
      </div>

      {showConfirm && (
        <ConfirmPaymentModal
          total={total}
          amountPaid={isDebtorsDirect ? Math.max(0, Math.min(total, Number(numericGiven) || 0)) : undefined}
          onCancel={() => setShowConfirm(false)}
          onConfirm={handleConfirm}
        />
      )}

      {showComplete && (
        <PaymentCompleteModal
          amountPaid={isDebtorsDirect ? amountPaidUsed : total}
          paymentMethod="Cash"
          discountApplied={0}
          onNewSale={() => {
            if (isDebtorsDirect) {
              setPrintOpen(false);
              setReceiptNo("");
              setShowComplete(false);
              navigate("/frontshop/debtors/transactions");
              return;
            }
            apiClearCart()
              .catch(() => { })
              .finally(() => {
                setPrintOpen(false);
                setReceiptNo("");
                setShowComplete(false);
                navigate("/frontshop");
              });
          }}
          onClose={() => {
            setShowComplete(false);
            if (isDebtorsDirect) navigate("/frontshop/debtors/transactions");
          }}
        />
      )}

      <PrintReceiptDialog
        open={printOpen}
        recieptno={receiptNo}
        onClose={() => setPrintOpen(false)}
      />
    </div>
  );
}
